export const PATIENT_FILE_CATEGORY_COLOR_TOKENS = [
  'teal',
  'coral',
  'amber',
  'green',
  'red',
  'muted',
] as const;

export type PatientFileCategoryColorToken =
  (typeof PATIENT_FILE_CATEGORY_COLOR_TOKENS)[number];

export const UNCATEGORIZED_FILE_CATEGORY_ID = 'uncategorized';
export const ALL_FILE_CATEGORIES_ID = 'all';

export function isPatientFileCategoryColorToken(
  value: string,
): value is PatientFileCategoryColorToken {
  return (PATIENT_FILE_CATEGORY_COLOR_TOKENS as readonly string[]).includes(value);
}

export function fileCategoryChipClass(token: string): string {
  const color = isPatientFileCategoryColorToken(token) ? token : 'muted';
  switch (color) {
    case 'teal':
      return 'bg-ds-teal-tint text-ds-teal';
    case 'coral':
      return 'bg-ds-coral-tint text-ds-coral-ink';
    case 'amber':
      return 'bg-ds-amber-tint text-ds-amber-ink';
    case 'green':
      return 'bg-ds-green-tint text-ds-green-ink';
    case 'red':
      return 'bg-ds-red-tint text-ds-red-ink';
    case 'muted':
      return 'bg-ds-neutral-tint text-ds-muted';
    default: {
      const _exhaustive: never = color;
      return _exhaustive;
    }
  }
}

export function fileCategorySwatchClass(token: string): string {
  const color = isPatientFileCategoryColorToken(token) ? token : 'muted';
  switch (color) {
    case 'teal':
      return 'bg-ds-teal';
    case 'coral':
      return 'bg-ds-coral';
    case 'amber':
      return 'bg-ds-amber';
    case 'green':
      return 'bg-ds-green';
    case 'red':
      return 'bg-ds-red';
    case 'muted':
      return 'bg-ds-muted';
    default: {
      const _exhaustive: never = color;
      return _exhaustive;
    }
  }
}

export interface FileCategoryLike {
  id: string;
  name: string;
  color_token: string;
  sort_order: number;
  is_archived: boolean;
}

export function resolveFileCategory<T extends FileCategoryLike>(
  categories: readonly T[],
  categoryId: string | null | undefined,
): T | null {
  if (!categoryId) return null;
  return categories.find((category) => category.id === categoryId) ?? null;
}

export function fileCategoryFilterCounts(
  files: readonly { category_id: string | null }[],
  categories: readonly { id: string }[],
): Record<string, number> {
  const counts: Record<string, number> = {
    [ALL_FILE_CATEGORIES_ID]: files.length,
    [UNCATEGORIZED_FILE_CATEGORY_ID]: 0,
  };
  for (const category of categories) {
    counts[category.id] = 0;
  }
  for (const file of files) {
    if (file.category_id && counts[file.category_id] != null) {
      counts[file.category_id] += 1;
    } else {
      counts[UNCATEGORIZED_FILE_CATEGORY_ID] += 1;
    }
  }
  return counts;
}

export function filterPatientFilesByCategory<T extends { category_id: string | null }>(
  files: readonly T[],
  filter: string,
): T[] {
  if (filter === ALL_FILE_CATEGORIES_ID) return [...files];
  if (filter === UNCATEGORIZED_FILE_CATEGORY_ID) {
    return files.filter((file) => !file.category_id);
  }
  return files.filter((file) => file.category_id === filter);
}

export function groupPatientFilesByCategory<T extends { category_id: string | null }>(
  files: readonly T[],
  categories: readonly FileCategoryLike[],
): Array<{ key: string; category: FileCategoryLike | null; files: T[] }> {
  const byId = new Map<string, T[]>();
  const uncategorized: T[] = [];
  for (const file of files) {
    if (file.category_id) {
      const existing = byId.get(file.category_id);
      if (existing) existing.push(file);
      else byId.set(file.category_id, [file]);
    } else {
      uncategorized.push(file);
    }
  }

  const groups: Array<{ key: string; category: FileCategoryLike | null; files: T[] }> = [];
  const ordered = [...categories].sort((a, b) => {
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    return a.name.localeCompare(b.name, 'da');
  });
  for (const category of ordered) {
    const grouped = byId.get(category.id);
    if (grouped?.length) {
      groups.push({ key: category.id, category, files: grouped });
    }
  }
  for (const [id, grouped] of byId) {
    if (ordered.some((category) => category.id === id)) continue;
    groups.push({
      key: id,
      category: { id, name: id, color_token: 'muted', sort_order: 999, is_archived: true },
      files: grouped,
    });
  }
  if (uncategorized.length > 0) {
    groups.push({ key: UNCATEGORIZED_FILE_CATEGORY_ID, category: null, files: uncategorized });
  }
  return groups;
}

export function assignableFileCategories<T extends FileCategoryLike>(
  categories: readonly T[],
): T[] {
  return categories
    .filter((category) => !category.is_archived)
    .sort((a, b) => {
      if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
      return a.name.localeCompare(b.name, 'da');
    });
}
